import React, { useMemo } from 'react';
import { Empty, Typography } from 'antd';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import dayjs from 'dayjs';

const { Text } = Typography;

const ChannelDistribution = ({ 
  channelData, 
  data, 
  comparisonMode = false, 
  comparisonPeriod = 'previous_month',
  dateRange,
  comparisonData = null 
}) => {
  // Process data for comparison mode  
  const processedChannelData = useMemo(() => {
    if (!channelData || channelData.length === 0) {
      return [];
    }

    if (!comparisonMode || !comparisonData) {
      return channelData;
    }

    // Generate channel data from comparison period
    const comparisonChannelData = [];
    if (comparisonData.data && comparisonData.data.length > 0) {
      const totals = comparisonData.data.reduce((acc, day) => {
        Object.keys(day).forEach(key => {
          if (key !== 'date') {
            acc[key] = (acc[key] || 0) + (parseFloat(day[key]) || 0);
          }
        });
        return acc;
      }, {});
      
      const channels = [
        { name: 'Google Reklamları', key: 'GADS COST (ALL)', color: '#FF4444' },
        { name: 'Meta CR', key: 'META CR', color: '#1890FF' },
        { name: 'Boost Reklamları', key: 'BOOST ADS', color: '#722ED1' },
        { name: 'Diğer Kanallar', key: 'OTHER', color: '#8C8C8C' }
      ];

      channels.forEach(channel => {
        if (channel.key === 'OTHER') {
          const totalCost = totals['COST(ALL)'] || 0;
          const metaCrCost = totals['META CR'] || 0;
          const googleCost = totals['GADS COST (ALL)'] || 0;
          const boostCost = totals['BOOST ADS'] || 0;
          const otherCost = Math.max(0, totalCost - metaCrCost - googleCost - boostCost);
          
          if (otherCost > 0) {
            comparisonChannelData.push({
              ...channel,
              value: otherCost
            });
          }
        } else {
          const value = totals[channel.key] || 0;
          if (value > 0) {
            comparisonChannelData.push({
              ...channel,
              value: value
            });
          }
        }
      });
    }

    return comparisonChannelData.length > 0 ? comparisonChannelData : channelData;
  }, [channelData, comparisonMode, comparisonData]);

  const periodLabels = {
    'previous_week': 'Önceki Hafta',
    'previous_month': 'Önceki Ay', 
    'previous_quarter': 'Önceki Çeyrek',
    'previous_year': 'Önceki Yıl',
    'same_period_last_year': 'Geçen Yılın Aynı Dönemi',
    'custom': 'Özel Tarih Aralığı'
  };

  if (!channelData || channelData.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100%', 
        minHeight: '200px' 
      }}>
        <Empty 
          description="Kanal dağılımı verisi bulunamadı" 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ margin: 0 }}
        />
      </div>
    );
  }

  // Toplam harcama hesapla
  const totalSpent = processedChannelData.reduce((sum, channel) => sum + channel.value, 0);
  const originalTotalSpent = channelData.reduce((sum, channel) => sum + channel.value, 0);
  const totalSpentText = new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(totalSpent);

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null; // %5'ten küçük değerleri gösterme
    
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize="11"
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const formatTooltipValue = (value, name, props) => [
    `${new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value)}`, 
    props.payload.name || name
  ];

  return (
    <div style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      padding: '0 8px'
    }}>
      {/* Header Bilgisi */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '12px',
        flexShrink: 0
      }}>
        <div style={{ 
          fontSize: '16px', 
          fontWeight: 'bold', 
          color: comparisonMode && comparisonData ? '#faad14' : '#52c41a',
          marginBottom: '4px'
        }}>
          {totalSpentText}
        </div>
        <Text type="secondary" style={{ fontSize: '11px' }}>
          {comparisonMode && comparisonData
            ? `${comparisonData.startDate} - ${comparisonData.endDate} (${comparisonData.data?.length || 0} gün toplamı)`
            : comparisonMode && !comparisonData
            ? 'Karşılaştırma verileri yükleniyor...'
            : `${data?.length || 0} gün toplamı`
          }
        </Text>
        {comparisonMode && comparisonData && (
          <div style={{ marginTop: '4px' }}>
            <Text style={{ fontSize: '10px', color: '#666' }}>
              Mevcut: {new Intl.NumberFormat('tr-TR', {
                style: 'currency',
                currency: 'TRY',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(originalTotalSpent)}
            </Text>
          </div>
        )}
      </div>

      {/* Pie Chart Container */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '180px',
        position: 'relative'
      }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
            <Pie
              data={processedChannelData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomizedLabel}
              outerRadius="85%"
              innerRadius="40%"
              fill="#8884d8"
              dataKey="value"
              startAngle={90}
              endAngle={450}
            >
              {processedChannelData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip 
              formatter={formatTooltipValue}
              labelFormatter={() => comparisonMode && comparisonData
                ? `Harcama Dağılımı - ${comparisonData.startDate} - ${comparisonData.endDate} (${comparisonData.data?.length || 0} gün)`
                : `Harcama Dağılımı (${data?.length || 0} gün)`
              }
              contentStyle={{ 
                backgroundColor: '#fff',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                fontSize: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div style={{ 
        flexShrink: 0,
        marginTop: '8px'
      }}>
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          justifyContent: 'center',
          gap: '8px'
        }}>
          {processedChannelData.map((item, index) => (
            <div 
              key={index}
              style={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '11px',
                color: '#666'
              }}
            >
              <div 
                style={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: item.color,
                  borderRadius: '2px',
                  marginRight: '4px'
                }}
              />
              <span>{item.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ChannelDistribution);